/**
 * Utility functions for LOR document display.
 *
 * Pure helpers extracted from StepActionHeader to keep the component focused
 * on orchestration.
 */
import {
  CoverageCategory,
  type CoverageSchema,
} from '@/services/types/coverage-types';

export type LorEmailTemplateKey = 'LOR_WITHOUT_PR' | 'LOR_WITH_PR';

export const LOR_CONTEXT_KEYS = {
  propertyAddress: [
    'accident_location',
    'incidentLocation',
    'property_address',
    'propertyAddress',
    'premises_address',
  ],
  carrierName: ['carrier_name', 'insuranceCarrier', 'carrierName'],
  namedInsured: ['named_insured', 'namedInsured'],
} as const;

export const getRecordString = (
  record: Record<string, unknown> | null | undefined,
  key: string,
): string | null => {
  const value = record?.[key];
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed || null;
};

export const getLorFieldValue = (
  doc: {
    merge_fields?: Record<string, unknown> | null;
    field_values?: Record<string, unknown> | null;
  },
  keys: readonly string[],
): string | null => {
  return (
    keys
      .map(
        (key) =>
          getRecordString(doc.merge_fields, key) ||
          getRecordString(doc.field_values, key),
      )
      .find(Boolean) ?? null
  );
};

export const formatCoverageCategoryLabel = (
  category: CoverageCategory | null | undefined,
): string | null => {
  if (!category) {
    return null;
  }
  return category
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const buildLorDocumentSubtitle = (
  doc: {
    merge_fields?: Record<string, unknown> | null;
    field_values?: Record<string, unknown> | null;
  },
  coverage: CoverageSchema | null | undefined,
): string => {
  const categoryLabel = formatCoverageCategoryLabel(coverage?.category);
  const propertyAddress = getLorFieldValue(doc, LOR_CONTEXT_KEYS.propertyAddress);
  const carrierName =
    coverage?.carrier?.name?.trim() || getLorFieldValue(doc, LOR_CONTEXT_KEYS.carrierName);
  const namedInsured =
    coverage?.named_insured?.[0]?.trim() ||
    getLorFieldValue(doc, LOR_CONTEXT_KEYS.namedInsured);

  const detail =
    coverage?.category === CoverageCategory.PROPERTY
      ? propertyAddress || namedInsured || carrierName
      : carrierName || namedInsured || propertyAddress;

  if (categoryLabel && detail) {
    return `${categoryLabel} • ${detail}`;
  }
  if (categoryLabel) {
    return categoryLabel;
  }
  if (detail) {
    return detail;
  }
  return 'Another matter LOR';
};
